import React, { memo, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Field, change } from "redux-form";

import { CheckButton, CustomInput } from "components";
import {
	commaAndFloat2Normalizer,
	floatWithComma2Normalizer,
	trimMidSentenceNormalizer,
	trimNormalizer,
} from "validators";
import {
	maxLength1000,
	normalizeAll,
	onlyAlphanumericAndSpecialCharacters,
	onlyDigits,
	required,
	validateWhenGiftLoyaltyEnabled,
} from "validators/validation";

import { ReactComponent as WarningIcon } from "static/images/warning-alert.svg";
import styles from "./additionalServices.module.scss";

let AdditionalServicesFields = ({
	formValues,
	formName,
	applicationId = "",
}) => {
	const dispatch = useDispatch();

	useEffect(() => {
		if (formValues?.giftLoyalty)
			dispatch(change(formName, "nailSoftGiftLoyalty", true));
		else dispatch(change(formName, "nailSoftGiftLoyalty", false));
	}, [dispatch, formName, formValues?.giftLoyalty]);

	const isCheckedBundles = formValues?.bundles;

	return (
		<>
			{/* will be implemented in RC-11698 */}
			{/* <Alert
				variant="notice"
				message={
					<span className="font-italic">
						Bundle PDF must be attached to the application at time
						of creation.
					</span>
				}
				className="mb-2"
			/> */}

			<Row className="mb-2 mt-3">
				<Col>
					<Field
						component={CheckButton}
						checked={isCheckedBundles}
						label="Bundles"
						id="bundles"
						name="bundles"
						hideRequiredMark
					/>
				</Col>
			</Row>
			<Row className={styles.mrl3}>
				<Col md={5}>
					<Field
						component={CustomInput}
						name="bundle1"
						label="Bundle 1"
						disabled={!isCheckedBundles}
						normalizeOnBlur={trimNormalizer}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						validate={isCheckedBundles && required}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						name="priceBundle1"
						label="Price 1, $"
						disabled={!isCheckedBundles}
						validate={isCheckedBundles && required}
						normalizeOnBlur={floatWithComma2Normalizer}
						normalize={commaAndFloat2Normalizer}
						solveCaretIssue={commaAndFloat2Normalizer}
						placeholder="0.00"
					/>
				</Col>
			</Row>
			<Row className={styles.mrl3}>
				<Col md={5}>
					<Field
						component={CustomInput}
						name="bundle2"
						label="Bundle 2"
						disabled={!isCheckedBundles}
						normalizeOnBlur={trimNormalizer}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						hideRequiredMark
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						name="priceBundle2"
						label="Price 2, $"
						disabled={!isCheckedBundles}
						hideRequiredMark
						normalizeOnBlur={floatWithComma2Normalizer}
						normalize={commaAndFloat2Normalizer}
						solveCaretIssue={commaAndFloat2Normalizer}
						placeholder="0.00"
					/>
				</Col>
			</Row>
			<Row className={`${styles.mrl3} mb-4`}>
				<Col md={12}>
					<Field
						smLabel={12}
						smSelect={12}
						component={CustomInput}
						as="textarea"
						wrapperClassName={"flex-column"}
						label={
							<span>
								Notes{" "}
								<small>
									(Bundle PDF must be attached to the
									application at time of creation)
								</small>
							</span>
						}
						name="noteBundle"
						normalizeOnBlur={trimNormalizer}
						normalize={normalizeAll([trimMidSentenceNormalizer])}
						disabled={!isCheckedBundles}
						hideRequiredMark
						validate={[maxLength1000]}
					/>
				</Col>
			</Row>

			<Row className="mb-3">
				<Col md={12} className="d-flex text-warning">
					<div className="pr-3">
						<WarningIcon width="16" />
					</div>
					<div>Additional documentation will be required.</div>
				</Col>
			</Row>
			<Row className="mb-3">
				<Col md={5}>
					<Field
						component={CheckButton}
						checked={formValues?.ach}
						label="ACH"
						id="ACH"
						name="ach"
						hideRequiredMark
					/>
				</Col>
			</Row>
			<Row className="mb-3">
				<Col>
					<Field
						component={CheckButton}
						checked={formValues?.cashAdvance}
						label="Cash Advance"
						id="cashAdvance"
						name="cashAdvance"
						hideRequiredMark
					/>
				</Col>
			</Row>
			<Row className="mb-3">
				<Col>
					<Field
						component={CheckButton}
						checked={formValues?.multiCurrency}
						label="Multi-Currency"
						id="multiCurrency"
						name="multiCurrency"
						hideRequiredMark
					/>
				</Col>
			</Row>
			<Row className="mb-3">
				<Col>
					<Field
						component={CheckButton}
						checked={formValues?.businessConnections}
						label="Business Connections"
						id="businessConnections"
						name="businessConnections"
						hideRequiredMark
					/>
				</Col>
			</Row>
			<Row className="mb-3">
				<Col>
					<Field
						component={CheckButton}
						checked={formValues?.checkServices}
						label="Check Services"
						id="checkServices"
						name="checkServices"
						hideRequiredMark
					/>
				</Col>
			</Row>
			<Row className="mb-2 mt-3">
				<Col>
					<Field
						component={CheckButton}
						checked={formValues?.giftLoyalty}
						label="Gift / Loyalty"
						id="giftLoyalty"
						name="giftLoyalty"
						hideRequiredMark
					/>
				</Col>
			</Row>
			<Row className={styles.mrl3}>
				<Col md={5}>
					<Field
						component={CustomInput}
						tabIndex="15"
						validate={validateWhenGiftLoyaltyEnabled}
						name="existingProvider"
						label="Existing Provider"
						disabled={!formValues?.giftLoyalty}
						normalizeOnBlur={trimNormalizer}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						tabIndex="16"
						validate={validateWhenGiftLoyaltyEnabled}
						normalize={onlyDigits}
						name="existingAcctNumber"
						label="Existing Acct #"
						disabled={!formValues?.giftLoyalty}
					/>
				</Col>
			</Row>
			<Row className={`${styles.mrl3} mb-4`}>
				<Col md={5}>
					<Field
						component={CheckButton}
						checked={formValues?.nailSoftGiftLoyalty}
						label="NailSoft Gift / Loyalty"
						id="nailSoftGiftLoyalty"
						name="nailSoftGiftLoyalty"
						disabled={!formValues?.giftLoyalty}
						hideRequiredMark
					/>
				</Col>
			</Row>
		</>
	);
};

const component = memo(AdditionalServicesFields);
export { component as AdditionalServicesFields };
